/**
 * Orchestrator — fetch regional infrastructure layers from the pinned
 * catalog and optional custom ArcGIS query URLs, returning SiteLayers
 * ready for merge into `site.siteLayers`.
 */
import { SITE_LAYER_MAX_COUNT } from '../siteLayers/siteLayerPersistence';
import {
  ARCGIS_LAYER_CATALOG,
  getCatalogEntry,
  type ArcgisCatalogEntry,
} from '../../config/arcgisLayerCatalog';
import { validateArcgisQueryUrl } from '../../lib/validateArcgisQueryUrl';
import type { SiteEntity, SiteLayer } from '../../types/site';
import {
  catalogEntryToConvertOptions,
  esriFeaturesToSiteLayers,
} from './esriJsonToSiteLayers';
import {
  envelopeFromBoundary,
  fetchArcgisFeatures,
  milesToMeters,
} from './fetchArcgisFeatures';
import { fetchPeeringDbFacilities } from './fetchPeeringDbFacilities';
import { refdataBbox, tryRefdata } from '../refdata/refdataClient';
import { bboxAroundPoint } from '../refdata/cellMath';
import { refdataFeatureToEsriFeature } from './esriJsonToSiteLayers';

const MAX_FEATURES_PER_SOURCE = 300;
// Must never exceed what persistence will ACCEPT. These were 1500 vs the
// persister's 450 (SITE_LAYER_MAX_COUNT), so any fetch returning 451+ layers
// succeeded, rendered, and then died on save with "A site may contain at most
// 450 map layers" — reported live 2026-08-05 fetching Redhead at 1 mile.
// Importing the constant keeps them from drifting apart again.
const MAX_TOTAL_LAYERS = SITE_LAYER_MAX_COUNT;
const CONCURRENCY = 1;

export interface FetchRegionalLayersArgs {
  site: Pick<SiteEntity, 'location' | 'siteBoundary' | 'siteCentroid'>;
  catalogEntryIds: string[];
  customUrls?: string[];
  radiusMiles: number;
  /** RD-1 (E) — refdata snapshots only; a dataset miss degrades to a
   *  warning instead of falling through to live ArcGIS. Used by the
   *  auto-seed path so a site scan never hammers flaky upstreams. */
  refdataOnly?: boolean;
}

export interface FetchRegionalLayersResult {
  layers: SiteLayer[];
  warnings: string[];
  fetchedBySource: Record<string, number>;
}

function resolveQueryCenter(
  site: FetchRegionalLayersArgs['site'],
): { lat: number; lng: number } {
  if (site.siteCentroid?.lat != null && site.siteCentroid?.lng != null) {
    return site.siteCentroid;
  }
  return site.location;
}

async function fetchCatalogEntry(
  entry: ArcgisCatalogEntry,
  lat: number,
  lng: number,
  radiusMiles: number,
  boundary: { lat: number; lng: number }[] | undefined,
  refdataOnly?: boolean,
): Promise<{ layers: SiteLayer[]; warning?: string }> {
  // RD-1 (E) — refdata-first for snapshot-backed entries. Conversion flows
  // through the SAME Esri→SiteLayer path as live fetches so ids/content
  // hashes stay dedup-compatible across live↔refdata switches.
  if (entry.refdataDataset) {
    const radiusMeters = milesToMeters(radiusMiles);
    const env = boundary && boundary.length >= 3
      ? envelopeFromBoundary(boundary, radiusMeters)
      : undefined;
    const bbox = env
      ? { minLat: env.ymin, minLng: env.xmin, maxLat: env.ymax, maxLng: env.xmax }
      : bboxAroundPoint({ lat, lng }, radiusMeters / 1000);
    const local = await tryRefdata(entry.refdataDataset, () =>
      refdataBbox(entry.refdataDataset!, bbox, MAX_FEATURES_PER_SOURCE));
    if (local) {
      const esriFeatures = local.features
        .map(refdataFeatureToEsriFeature)
        .filter((f): f is NonNullable<typeof f> => f != null);
      const opts = catalogEntryToConvertOptions(entry);
      const layers = esriFeaturesToSiteLayers(esriFeatures, { ...opts, dataAsOf: local.vintageDate });
      const warning = layers.length === 0
        ? `No features returned for ${entry.label}`
        : local.truncated
          ? `${entry.label}: capped at ${MAX_FEATURES_PER_SOURCE} features — narrow the radius for full coverage`
          : undefined;
      return { layers, warning };
    }
    // Snapshot miss: refdata-only entries and refdata-only mode degrade to
    // a warning; entries with a live queryUrl fall through below.
    if (refdataOnly || !entry.queryUrl) {
      return { layers: [], warning: `${entry.label}: refdata dataset not seeded — run the matching refdata:ingest CLI` };
    }
  } else if (refdataOnly) {
    return { layers: [], warning: `${entry.label}: no refdata dataset for this entry` };
  }

  if (entry.kind === 'peeringdb') {
    const result = await fetchPeeringDbFacilities({
      lat,
      lng,
      radiusMiles,
      maxFeatures: MAX_FEATURES_PER_SOURCE,
    });
    let warning = result.warning;
    if (result.layers.length === 0) {
      warning = warning ?? `No colocation facilities within ${radiusMiles} mi — try a larger radius`;
    }
    return { layers: result.layers, warning };
  }

  if (!entry.queryUrl) {
    throw new Error(`Catalog entry ${entry.id} has no queryUrl`);
  }

  const radiusMeters = milesToMeters(radiusMiles);
  const envelope =
    boundary && boundary.length >= 3
      ? envelopeFromBoundary(boundary, radiusMeters)
      : undefined;

  const { features, warning } = await fetchArcgisFeatures({
    lat,
    lng,
    radiusMeters,
    queryUrl: entry.queryUrl,
    outFields: entry.outFields ?? '*',
    maxFeatures: MAX_FEATURES_PER_SOURCE,
    envelope,
    label: entry.label,
  });

  const opts = catalogEntryToConvertOptions(entry);
  const layers = esriFeaturesToSiteLayers(features, opts);
  if (features.length === 0) {
    return { layers, warning: warning ?? `No features returned for ${entry.label}` };
  }
  return { layers, warning };
}

async function fetchCustomUrl(
  url: string,
  lat: number,
  lng: number,
  radiusMiles: number,
  boundary: { lat: number; lng: number }[] | undefined,
  index: number,
): Promise<{ layers: SiteLayer[]; warning?: string; sourceKey: string }> {
  const validation = validateArcgisQueryUrl(url);
  if (!validation.ok || !validation.normalizedUrl) {
    throw new Error(validation.error ?? 'Invalid custom URL');
  }

  const sourceKey = `custom-${index}`;
  const radiusMeters = milesToMeters(radiusMiles);
  const envelope =
    boundary && boundary.length >= 3
      ? envelopeFromBoundary(boundary, radiusMeters)
      : undefined;

  const { features, warning } = await fetchArcgisFeatures({
    lat,
    lng,
    radiusMeters,
    queryUrl: validation.normalizedUrl,
    outFields: '*',
    maxFeatures: MAX_FEATURES_PER_SOURCE,
    envelope,
  });

  const layers = esriFeaturesToSiteLayers(features, {
    catalogId: sourceKey,
    category: 'other',
    source: 'arcgis-import',
    labelPrefix: 'Custom layer',
    sourceUrl: validation.normalizedUrl,
  });

  if (features.length === 0) {
    return {
      layers,
      warning: warning ?? 'Custom URL returned no features',
      sourceKey,
    };
  }
  return { layers, warning, sourceKey };
}

/**
 * Fit per-source layer lists into the site-wide layer budget.
 *
 * The pre-2026-08 cap was `allLayers.splice(450)` in CATALOG order — a
 * dense electrical fetch consumed the whole budget and every source after
 * it (wetlands, flood zones, protected areas, …) was fetched, counted in
 * the preview, and then silently discarded. Symptom: "I checked
 * everything but only see electrical."
 *
 * Strategy: sort each source's layers nearest-first, then round-robin one
 * layer at a time across sources until the budget is spent. Every
 * selected source keeps its closest features; dense sources absorb the
 * cuts instead of starving whoever came later in the catalog.
 */
export function allocateLayerBudget(
  sources: Array<{ key: string; label: string; layers: SiteLayer[] }>,
  center: { lat: number; lng: number },
  budget: number,
): { layers: SiteLayer[]; warnings: string[] } {
  const total = sources.reduce((n, s) => n + s.layers.length, 0);
  if (total <= budget) {
    return { layers: sources.flatMap(s => s.layers), warnings: [] };
  }

  const distanceSq = (l: SiteLayer): number => {
    const p = l.geometry.coordinates[0];
    if (!p) return Number.POSITIVE_INFINITY;
    const dLat = p.lat - center.lat;
    const dLng = (p.lng - center.lng) * Math.cos((center.lat * Math.PI) / 180);
    return dLat * dLat + dLng * dLng;
  };

  const queues = sources.map(s => ({
    ...s,
    ranked: s.layers
      .map(layer => ({ layer, d: distanceSq(layer) }))
      .sort((a, b) => a.d - b.d)
      .map(({ layer }) => layer),
    taken: 0,
  }));

  const kept: SiteLayer[] = [];
  while (kept.length < budget) {
    let progressed = false;
    for (const q of queues) {
      if (kept.length >= budget) break;
      if (q.taken >= q.ranked.length) continue;
      kept.push(q.ranked[q.taken]);
      q.taken += 1;
      progressed = true;
    }
    if (!progressed) break;
  }

  const warnings: string[] = [];
  for (const q of queues) {
    if (q.taken < q.ranked.length) {
      warnings.push(
        `${q.label}: kept the ${q.taken} nearest of ${q.ranked.length} features (${budget}-layer site cap)`,
      );
    }
  }
  return { layers: kept, warnings };
}

/** Run async tasks with bounded concurrency. */
async function mapPool<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;

  async function worker(): Promise<void> {
    while (next < items.length) {
      const i = next++;
      results[i] = await fn(items[i], i);
    }
  }

  const workers = Array.from({ length: Math.min(limit, items.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

export async function fetchRegionalLayers(
  args: FetchRegionalLayersArgs,
): Promise<FetchRegionalLayersResult> {
  const { lat, lng } = resolveQueryCenter(args.site);
  const boundary =
    args.site.siteBoundary && args.site.siteBoundary.length >= 3
      ? args.site.siteBoundary
      : undefined;

  const warnings: string[] = [];
  const fetchedBySource: Record<string, number> = {};
  const sourceLists: Array<{ key: string; label: string; layers: SiteLayer[] }> = [];

  const entries = args.catalogEntryIds
    .map(id => getCatalogEntry(id))
    .filter((e): e is ArcgisCatalogEntry => e != null);

  const unknownIds = args.catalogEntryIds.filter(id => !getCatalogEntry(id));
  if (unknownIds.length > 0) {
    warnings.push(`Unknown catalog entries skipped: ${unknownIds.join(', ')}`);
  }

  const catalogResults = await mapPool(entries, CONCURRENCY, async entry => {
    try {
      return await fetchCatalogEntry(entry, lat, lng, args.radiusMiles, boundary, args.refdataOnly);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      const warning = msg.toLowerCase().includes(entry.label.toLowerCase())
        ? msg
        : `${entry.label}: ${msg}`;
      return { layers: [] as SiteLayer[], warning };
    }
  });

  entries.forEach((entry, i) => {
    const result = catalogResults[i];
    fetchedBySource[entry.id] = result.layers.length;
    if (result.warning) warnings.push(result.warning);
    sourceLists.push({ key: entry.id, label: entry.label, layers: result.layers });
  });

  const customUrls = (args.customUrls ?? []).map(u => u.trim()).filter(Boolean);
  if (customUrls.length > 0) {
    const customResults = await mapPool(customUrls, CONCURRENCY, async (url, i) => {
      try {
        return await fetchCustomUrl(url, lat, lng, args.radiusMiles, boundary, i);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        warnings.push(`Custom URL ${i + 1}: ${msg}`);
        return {
          layers: [] as SiteLayer[],
          warning: msg,
          sourceKey: `custom-${i}`,
        };
      }
    });

    customResults.forEach((result, i) => {
      fetchedBySource[result.sourceKey] = result.layers.length;
      if (result.warning) warnings.push(result.warning);
      sourceLists.push({ key: result.sourceKey, label: `Custom URL ${i + 1}`, layers: result.layers });
    });
  }

  const allocated = allocateLayerBudget(sourceLists, { lat, lng }, MAX_TOTAL_LAYERS);
  // Cap warnings lead — they explain missing map data, which outranks
  // per-source fetch niggles in the (truncated) toast.
  warnings.unshift(...allocated.warnings);

  return { layers: allocated.layers, warnings, fetchedBySource };
}

/** Default catalog entry ids that ship enabled in the UI. */
export function defaultEnabledCatalogIds(): string[] {
  return ARCGIS_LAYER_CATALOG.filter(e => e.defaultEnabled).map(e => e.id);
}

// Sub-mile options added 2026-08-05: a 1 mi fetch on a dense site returns
// hundreds of features and hits the layer cap, so parcel-scale screening
// needs finer radii. Fractions are exact eighths so the labels below are
// honest (0.125 = 1/8 mi, not a rounded approximation).
export const REGIONAL_RADIUS_OPTIONS_MILES = [0.125, 0.25, 0.5, 1, 5, 10, 25, 50] as const;

/** Default radius. 1/4 mi keeps a first fetch inside the layer cap on dense
 *  sites; wider radii remain one selection away. */
export const DEFAULT_REGIONAL_RADIUS_MILES = 0.25;

/** Fraction-aware label — "0.125 mi" reads as noise next to "1 mi". */
export function formatRadiusMiles(miles: number): string {
  const fractions: Record<string, string> = { '0.125': '⅛', '0.25': '¼', '0.5': '½' };
  return `${fractions[String(miles)] ?? miles} mi`;
}
